import Vue           from 'vue'
import Vuex          from 'vuex'
import axios         from 'axios';
import router        from '../router/index.js';

import {
	Auth,
	SystemConfigService,
	CountryService,
	NotificationsService,
	FavoritesService,
	UserService,
} from '@/services';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    notificationItems: [],
    usStates: [],
    countries: [],
    openRequestCount: 0,
    currentUser: {},
    token: null,
    cancelToken: {},
    customerJdeNumber: null,
    currentOrderNumber: null,
    currentOrderReadOnly: null,
    saveTrigger: true,
    alertMessage: null,
    alertClosed: false,
    alertBackgroundColor: 'rgb(232,63,84)',
    notifications: null,
    isPrivateCustomer: false,
    favorites: [],
    isLoggingOut: false,
    deploymentLockActive: false,
    deploymentLockMessage: '',
  },
  mutations: {
    TOGGLE_SAVE_TRIGGER(state){
      state.saveTrigger = !state.saveTrigger
    },
    UPDATE_ALERT_MESSAGE(state, message){
      state.alertMessage = message;
    },
    UPDATE_ALERT_MESSAGE_CLOSED(state, closed){
      state.alertMessageClosed = closed;
    },
    UPDATE_ALERT_BACKGROUND_COLOR(state, color){
      state.alertBackgroundColor = color;
    },
    UPDATE_USER_NOTIFICATIONS(state, notifications){
      state.notifications = notifications;
    },
		UPDATE_USER_REDNEWSICON(state, showRedNewsIcon){
			state.currentUser.ShowRedNewsIcon = showRedNewsIcon;
		},
    UPDATE_ORDER_INFO(state, info){
      state.currentOrderNumber = info.number
      state.currentOrderReadOnly = info.readOnly
    },
    SET_NOTIFICATION_ITEM(state, message){
      state.notificationItems.push(message);
    },
    SET_CUSTOMER_JDE_NUMBER(state, number){
      state.customerJdeNumber = number
    },
    SET_CUSTOMER_IS_PRIVATE(state, isPrivate){
      state.isPrivateCustomer = isPrivate;
    },
    DELETE_NOTIFICATION_ITEM(state, notificationId){
        let itemIndex = state.notificationItems.findIndex(item => item.id === notificationId);
        state.notificationItems.splice(itemIndex,1);
    },
    ADD_REQUEST(state){
      state.openRequestCount++;
    },
    REMOVE_REQUEST(state){
      state.openRequestCount--;
    },
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_USER(state, user){
      state.currentUser = user;
    },
    SET_US_STATES(state, usStates){
      state.usStates = usStates;
    },
    SET_COUNTRIES(state, countries){
      state.countries = countries;
    },
    // SET_REPORT_TYPES(state, reportTypes){
    //   state.reportTypes = reportTypes;
    // },
    // SET_PRINT_OPTIONS(state, printOptions){
    //   state.printOptions = printOptions;
    // },
    UPDATE_DATASOURCE_CANCEL_TOKEN(state, token){
      if(state.cancelToken.cancel){
        state.cancelToken.cancel()
      }
      state.cancelToken = token;
    },
    SET_FAVORITES(state, favorites) {
      state.favorites = favorites
    },
    SET_LOGGING_OUT(state, isLoggingOut) {
      state.isLoggingOut = isLoggingOut
    },
    SET_DEPLOYMENT_LOCK(state, { isActive, message }) {
      state.deploymentLockActive = isActive;
      state.deploymentLockMessage = message || '';
    },
  },
  actions: {
    TOGGLE_SAVE_TRIGGER({commit}){
      commit('TOGGLE_SAVE_TRIGGER')
    },
    GET_ALERT_MESSAGE({commit, state, dispatch}){
      // Allow announcement API call even without token (for login page announcements)
      // The SystemConfig/SystemAnnouncement endpoint is [AllowAnonymous]

      SystemConfigService.getMessage().then(res => {
        commit('UPDATE_ALERT_MESSAGE', res)

        // Always update background color
        localStorage.setItem('alertBackgroundColor', res?.BackgroundColor)
        commit('UPDATE_ALERT_BACKGROUND_COLOR', res?.BackgroundColor ?? 'rgb(232,63,84)')

        if(res) {
          // Get current stored values
          const currentMessage = localStorage.getItem('alertMessage');
          const currentMessageBody = localStorage.getItem('alertMessageBody');
          const currentMessageTitle = localStorage.getItem('alertMessageTitle');
          const currentExpiration = localStorage.getItem('alertMessageExpiration');
          const currentOneTime = localStorage.getItem('alertMessageIsOneTime');

          // Check if any key announcement data has changed
          const messageChanged = res.Message !== currentMessage;
          const bodyChanged = res.MessageBody !== currentMessageBody;
          const titleChanged = res.MessageTitle !== currentMessageTitle;
          const expirationChanged = res.ExpirationDate !== currentExpiration;
          const oneTimeChanged = res.IsOneTimeMessage?.toString() !== currentOneTime;

          // Also check if this is the first time we're seeing this announcement (no localStorage data)
          const isFirstTime = !currentMessage && !currentMessageBody && !currentMessageTitle;

          const announcementChanged = messageChanged || bodyChanged || titleChanged || expirationChanged || oneTimeChanged || isFirstTime;

          // Always update localStorage with current values
          localStorage.setItem('alertMessage', res.Message || '')
          localStorage.setItem('alertMessageBody', res.MessageBody || '')
          localStorage.setItem('alertMessageTitle', res.MessageTitle || '')
          localStorage.setItem('alertMessageExpiration', res.ExpirationDate || '')
          localStorage.setItem('alertMessageIsOneTime', res.IsOneTimeMessage?.toString() || 'false')
          localStorage.setItem('alertMessageIncludeOnLogonScreen', res.IncludeOnLogonScreen?.toString() || 'false')

          // If announcement changed, reset the closed state so users see the new/updated message
          if(announcementChanged) {
            localStorage.setItem('alertMessageClosed', 'false')
            commit('UPDATE_ALERT_MESSAGE_CLOSED', false)
          }
        } else {
          // No announcement available, ensure closed state is set
          localStorage.setItem('alertMessageClosed', 'true')
          commit('UPDATE_ALERT_MESSAGE_CLOSED', true)
        }
      }).catch(err => {
        console.log('Error getting alert message:', err);
      })

      // Check deployment lock status — piggybacks on the existing 60-second banner poll.
      // If active and the current user is not exempt, redirect them to the login page.
      SystemConfigService.getDeploymentLock().then(lockRes => {
        const isActive = lockRes?.isActive === true;
        commit('SET_DEPLOYMENT_LOCK', { isActive, message: lockRes?.message });

        // Only redirect if the current user is fully loaded (AppUserId is populated).
        // This prevents a race condition where the poll fires during login before
        // GET_CURRENT_USER has completed and IsExemptAdmin is not yet set.
        if (isActive && state.token && state.currentUser?.AppUserId && !state.currentUser?.IsExemptAdmin) {
          // Clear the browser-level beforeunload guard (set by CustomerOrder)
          // so no "are you sure" dialog interrupts the redirect.
          window.onbeforeunload = null;
          dispatch('AUTH_LOGOUT');
        }
      }).catch(err => {
        console.log('Error checking deployment lock:', err);
      })
    },
    SET_ALERT_MESSAGE_CLOSED({commit}, closed){
      commit('UPDATE_ALERT_MESSAGE_CLOSED', closed)
    },
    GET_USER_NOTIFICATIONS({commit, state}){
      if(!state.token) return;
      NotificationsService.get().then(res => {
        commit('UPDATE_USER_NOTIFICATIONS', res.Notifications)
				commit('UPDATE_USER_REDNEWSICON', res.ShowRedNewsIcon);
      }).catch(err => {
        console.log(err);
      })
    },
		RESET_USER_REDNEWSICON({commit}){
			UserService.turnRedNewsIconOff().then(res => {
				commit('UPDATE_USER_REDNEWSICON', false);
			}).catch(err => {
				console.log(err);
			})
		},
    UPDATE_ORDER_INFO({commit}, info){
      commit('UPDATE_ORDER_INFO', info)
    },
    UPDATE_DATASOURCE_CANCEL_TOKEN({commit}, token){
      commit('UPDATE_DATASOURCE_CANCEL_TOKEN', token)
    },
    ADD_NOTIFICATION({ commit, dispatch }, messageInfo){
      let timeout = messageInfo.noTimeout ? false : messageInfo.timeout ? messageInfo.timeout : 6000;
      let id = Math.random().toString(36).substring(2) + Date.now().toString(36);
      let message = { ...messageInfo, timeout, id };
      commit('SET_NOTIFICATION_ITEM', message);
      if(timeout){
        setTimeout(() => {
          dispatch('REMOVE_NOTIFICATION', message.id);
        }, message.timeout);
      }
    },
    REMOVE_NOTIFICATION({ commit }, notificationId){
      commit('DELETE_NOTIFICATION_ITEM', notificationId);
    },
    SET_CUSTOMER_JDE_NUMBER({commit}, customerJdeNumber){
      commit('SET_CUSTOMER_JDE_NUMBER', customerJdeNumber)
    },
    SET_CUSTOMER_IS_PRIVATE({commit}, isPrivate){
      commit('SET_CUSTOMER_IS_PRIVATE', isPrivate)
    },
    SET_TOKEN({commit, dispatch}, token) {
      return new Promise( (resolve,reject) => {
          commit('SET_TOKEN', token);
          addTokenToAxiosHeaders(token.access_token);
          localStorage.setItem('token', JSON.stringify(token));
          sessionStorage.removeItem('orderSearchStatus');
          // localStorage.setItem('alertMessageClosed', false)
          dispatch('GET_CURRENT_USER').then(res => {
            resolve();
          }).catch(err => {
            reject();
          });
      });
    },
    GET_US_STATES({ commit, dispatch }, countryId = 1){
      CountryService.states(countryId).then(res => {
        commit('SET_US_STATES', res);
      }).catch(err => {
        dispatch('ADD_NOTIFICATION', { text: err.data.message, type: 'error' });
      })
    },
    GET_COUNTRIES({ commit, dispatch }){
      CountryService.all().then(res => {
        commit('SET_COUNTRIES', res);
      }).catch(err => {
        dispatch('ADD_NOTIFICATION', { text: err.data.message, type: 'error' });
      })
    },
    // GET_REPORT_TYPES({ commit, dispatch }){
    //   ReportService.types().then(res => {
    //     commit('SET_REPORT_TYPES', res)
    //   }).catch(err => {
    //     dispatch('ADD_NOTIFICATION', { text: err.response.data.Message , type: 'error' });
    //   })
    // },
    // GET_PRINT_OPTIONS({ commit, dispatch }){
    //   OrderPrintOptionsService.all()
    //     .then(res => {
    //       commit('SET_PRINT_OPTIONS', res);
    //     }) .catch(err => {
    //       dispatch('ADD_NOTIFICATION', { text: err.response.data.Message , type: 'error' });
    //     })
    // },
    GET_CURRENT_USER({commit, dispatch}){
      return new Promise( (resolve, reject) => {
          Auth.currentUser().then(user => {
            commit('SET_USER', user);
            //console.debug('CURRENT USER: ', user);
            // dispatch('GET_REPORT_TYPES');
            // dispatch('GET_PRINT_OPTIONS');
            resolve();
          }).catch(err => {
            // Only log unexpected errors, not authentication failures
            if (err.message !== 'User not authenticated') {
              console.error('Got Error: ', err);
            }
            reject();
          });
      });
    },
    AUTH_LOGOUT({commit}){
      // Set flag to indicate we're in the logout process
      commit('SET_LOGGING_OUT', true);

      // basically, no matter if the backend successfully invalidates the token, clear
      // token from memory state and redirect to login page.
      Auth.logout().finally(() => {
        commit('SET_TOKEN', null);
        commit('SET_USER', {});
        localStorage.removeItem('token');
        localStorage.removeItem('detailView');
				sessionStorage.removeItem('orderSearchState');
        sessionStorage.removeItem('orderSearchStatus');
        //localStorage.removeItem('alertMessage');
        //localStorage.removeItem('alertMessageClosed');
        removeTokenFromAxiosHeaders();
        commit('SET_LOGGING_OUT', false);
        router.push({name:'VisitorHome'});
      });
    },
    UPDATE_FAVORITES({commit}) {
      FavoritesService.getFavorites()
        .then(res => {
          commit('SET_FAVORITES', res);
        })
    }
  }
})

function addTokenToAxiosHeaders(access_token) {
   axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
}

function removeTokenFromAxiosHeaders() {
   delete axios.defaults.headers.common['Authorization'];
}


